import { useMemo } from "react"

import type { ConversationKeysetRowResource } from "@/types_api/backend/transport/uploaded_conversation_keyset_response"
import { normalizeConversationTransportRow } from "@/utils/conversationsTransportRows"

import { VirtualConversationsTable } from "./VirtualConversationsTable"

const columns = [
  { key: "audio", label: "Audio" },
  { key: "created_at", label: "Uploaded", width: "180px" },
  { key: "occurred_at", label: "Occurred", width: "180px" },
  { key: "customer_metadata", label: "Metadata" },
  { key: "simulation_output_id", label: "ID" },
]
const visibleColumns = new Set(columns.map((column) => column.key))
const metricRenderersById = {}

interface MultiSelection {
  /** Picked simulation output ids. */
  selectedIds: Set<string>
  onSelectionChange: (ids: Set<string>) => void
  /**
   * Once this many rows are picked, the rest are disabled. Rows without a run
   * id are always disabled.
   */
  maxSelected: number
}

/**
 * Uploaded conversations list for picking dialogs. With `onSelect` a row click
 * picks one conversation; with `multiSelect` every row shows a checkbox and a
 * row click toggles it.
 */
export function ConversationSelectionTable({
  items,
  selectedId,
  onSelect,
  multiSelect,
  isLoading,
  hasMore,
  isFetchingMore,
  onLoadMore,
}: {
  items: ConversationKeysetRowResource[]
  selectedId?: string | null
  onSelect?: (id: string) => void
  multiSelect?: MultiSelection
  isLoading: boolean
  hasMore: boolean
  isFetchingMore: boolean
  onLoadMore: () => void
}) {
  const rows = useMemo(
    () => items.map(normalizeConversationTransportRow),
    [items],
  )
  const isAtLimit = multiSelect
    ? multiSelect.selectedIds.size >= multiSelect.maxSelected
    : false

  const handleRowClick = (id: string) => {
    if (!multiSelect) {
      onSelect?.(id)
      return
    }
    const next = new Set(multiSelect.selectedIds)
    if (next.has(id)) {
      next.delete(id)
    } else if (!isAtLimit) {
      next.add(id)
    }
    multiSelect.onSelectionChange(next)
  }

  return (
    <VirtualConversationsTable
      displayControls={false}
      rows={rows}
      columns={columns}
      visibleColumns={visibleColumns}
      metricRenderersById={metricRenderersById}
      isMetricsLoading={false}
      activeRowId={multiSelect ? undefined : selectedId}
      selectedKeys={multiSelect?.selectedIds}
      onSelectionChange={multiSelect?.onSelectionChange}
      isRowSelectionDisabled={
        multiSelect
          ? (row) =>
              !row.run_id ||
              (isAtLimit &&
                !multiSelect.selectedIds.has(row.simulation_output_id ?? ""))
          : undefined
      }
      pickerSelection={Boolean(multiSelect)}
      onRowClick={handleRowClick}
      isLoading={isLoading}
      hasNextPage={hasMore}
      isFetchingNextPage={isFetchingMore}
      onFetchNextPage={onLoadMore}
    />
  )
}
